import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../utils/AuthContext.js";
import { useNavigation } from "../utils/NavigationContext.js";
import { useToast } from "./Toast.js";
import { formatStatus, getStatusColor } from "../utils/statusFormatter.js";
import axios from "axios";
import moment from "moment";
import html2pdf from "html2pdf.js";
import {
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  X,
  Loader,
  Check,
  XCircle,
  Send,
  RefreshCw,
} from "lucide-react";
import DashboardLoader from "./loader/DashboardLoader.js";

import Sidebar from "./Sidebar";
import VideoSubmitModal from "./VideoSubmitModal.js";
import { PurchaseOrderPreview } from "./purchaseOrderPreview.js";
import VideoCard from "./VideoCard.js";
import MonthSelector from "./MonthSelector.js";

const ViewVideos = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { endNavigation } = useNavigation();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false); // Don't show main loading screen initially
  const [error, setError] = useState(null);
  const [expandedVideo, setExpandedVideo] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPreview, setIsPreview] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  // Get initial month from URL params (no default)
  const getInitialMonth = () => {
    const year = searchParams.get('year');
    const month = searchParams.get('month');

    if (year && month) {
      // Validate the date
      const dateStr = `${year}-${month.padStart(2, '0')}`;
      if (moment(dateStr, 'YYYY-MM', true).isValid()) {
        return dateStr;
      }
    }

    return null; // Return null if no valid month in URL
  };

  const initialMonth = getInitialMonth();
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [monthSelectionMode, setMonthSelectionMode] = useState(!initialMonth); // Show month selector if no initial month
  const [monthLoading, setMonthLoading] = useState(!initialMonth); // Start loading if in selection mode (no initial month)
  const { userRole } = useAuth();

  // Memoize filtered videos for better performance
  const poGeneratedVideos = useMemo(
    () => videos.filter(video => video.status === "PO_GENERATED"),
    [videos]
  );

  const submissionOpenVideos = useMemo(
    () => videos.filter(video => video.status === "SUBMISSION_OPEN"),
    [videos]
  );

  const approvedForPoVideos = useMemo(
    () => videos.filter(video => video.status === "APPROVED_FOR_PO"),
    [videos]
  );

  useEffect(() => {
    // Only fetch available months when in month selection mode or when we have a selected month
    if (monthSelectionMode || selectedMonth) {
      fetchAvailableMonths();
    }
  }, []);

  useEffect(() => {
    // Only fetch videos when month is selected and not in selection mode
    if (selectedMonth && !monthSelectionMode) {
      fetchVideosAndUser();
    }
  }, [selectedMonth, monthSelectionMode]);

  const fetchAvailableMonths = async () => {
    try {
      setMonthLoading(true);
      // Use the new endpoint to get available months
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-available-months`,
        { withCredentials: true }
      );

      const monthsList = response.data;
      setAvailableMonths(monthsList);

      // Only auto-select if we already have a selected month (not in selection mode)
      if (monthsList.length > 0 && selectedMonth && !monthSelectionMode) {
        // Check if the current selected month exists in available months
        const monthExists = monthsList.some(m => m.value === selectedMonth);

        if (!monthExists) {
          // If current month doesn't exist in data, go back to selection mode
          setSelectedMonth(null);
          setMonthSelectionMode(true);
          setSearchParams({}); // Clear URL params
        } else {
          // If month exists, update the URL to ensure it's set
          const [year, month] = selectedMonth.split('-');
          setSearchParams({ year, month });
        }
      }
    } catch (err) {
      console.error("Error fetching available months:", err);
      // If error, use default last 6 months
      const defaultMonths = [];
      for (let i = 0; i < 6; i++) {
        const date = moment().subtract(i, 'months');
        defaultMonths.push({
          value: date.format('YYYY-MM'),
          label: date.format('MMMM YYYY'),
          month: date.month() + 1,
          year: date.year()
        });
      }
      setAvailableMonths(defaultMonths);
    } finally {
      setMonthLoading(false);
      endNavigation(); // End navigation loading if we're in month selection mode
    }
  };

  const fetchVideos = async (monthFilter) => {
    try {
      // Use server-side filtering with month and year parameters
      let url = `${process.env.REACT_APP_API_URL}/get-all-videos`;
      const params = [];

      if (monthFilter) {
        const [year, month] = monthFilter.split('-');
        params.push(`year=${year}`);
        params.push(`month=${month}`);
      }

      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }

      const response = await axios.get(url, { withCredentials: true });
      return response.data;
    } catch (err) {
      console.error("Error fetching videos:", err.message);
      throw new Error("Failed to fetch videos. Please try again later.");
    }
  };

  const fetchVideosAndUser = async () => {
    try {
      setLoading(true); // Show loading while fetching videos
      const response = await fetchVideos(selectedMonth);

      // Fetch user only once
      if (!user) {
        const userResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/get-user`,
          { withCredentials: true }
        );
        setUser(userResponse.data);
      }

      setVideos(response);
      setError(null);
    } catch (err) {
      showToast("Failed to fetch videos. Please try again later.", "error");
      console.error("Error fetching videos:", err.message);
    } finally {
      setLoading(false);
      endNavigation(); // End navigation loading when data is ready
    }
  };

  const handleInitialMonthSelection = (monthValue) => {
    // When user selects a month from the initial selection screen
    const [year, month] = monthValue.split('-');
    setSelectedMonth(monthValue);
    setSearchParams({ year, month });
    setMonthSelectionMode(false);
  };

  const handleMonthChange = (newMonth) => {
    setSelectedMonth(newMonth);

    // Update URL with new month and year
    const [year, month] = newMonth.split('-');
    setSearchParams({ year, month });
  };

  const handleDelete = async (video) => {
    try {
      setLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/delete-video/${video._id}/${video.plan._id}`,
        {
          withCredentials: true,
        },
      );
      setVideos(videos?.filter((v) => v._id !== video._id));
      showToast("Video deleted successfully", "success");
    } catch (err) {
      showToast("Failed to delete video. Please try again.", "error");
      console.error("Error deleting video:", err);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setVideoToDelete(null);
    }
  };
  const handleGenerateInvoice = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-all-videos?invoiceGenerated=false`,
        {
          withCredentials: true,
        },
      );
      if (response.data.length === 0) {
        setLoading(false);
        showToast("No videos to generate purchase order", "warning");
        return;
      }
      setInvoiceData(response.data);
      setLoading(false);
      setPreview(true);
    }
    catch (err) {
      showToast("Failed to generate invoice. Please try again.", "error");
      console.error("Error generating invoice:", err);
      setLoading(false);
    }
  };

  const handleVideoReview = async () => {
    try {
      if (!selectedMonth) {
        showToast("Please select a month first", "warning");
        return;
      }

      setLoading(true);

      // Extract month and year from selectedMonth (format: 'YYYY-MM')
      const [year, monthNum] = selectedMonth.split('-');
      const monthName = moment(`${year}-${monthNum}`, 'YYYY-MM').format('MMMM');

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/review-submitted-videos`,
        {
          month: monthName,
          year: year,
        },
        {
          withCredentials: true,
        },
      );
      if (response.data.length === 0) {
        setLoading(false);
        showToast("No videos to generate purchase order", "warning");
        return;
      }
      setInvoiceData(response.data);
      setLoading(false);
      setPreview(true);
      showToast(`Video review completed for ${monthName} ${year}`, "success");
    }
    catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to review videos. Please try again.";
      showToast(errorMessage, "error");
      console.error("Error reviewing videos:", err);
      setLoading(false);
    }
  }

  const handleUnapproveVideo = async (video) => {
    try {
      setLoading(true);

      const formattedData = {
        videoId: video._id,
        videos: {
          status: "PENDING_APPROVAL",
        },
      };

      await axios.put(
        `${process.env.REACT_APP_API_URL}/update-video/`,
        formattedData,
        {
          withCredentials: true,
        },
      );
      fetchVideosAndUser();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showToast("Failed to approve video. Please try again.", "error");
      console.error("Error approving video:", err);
    }
  };

  const handleDownloadPurchaseOrder = async () => {
    try {
      if (!selectedMonth) {
        showToast("Please select a month first", "warning");
        return;
      }

      setLoading(true);

      const [year, monthNum] = selectedMonth.split('-');
      const monthName = moment(`${year}-${monthNum}`, 'YYYY-MM').format('MMMM');

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-purchase-order-by-month?month=${monthName}&year=${year}`,
        { withCredentials: true }
      );

      if (!response.data || !response.data.html) {
        showToast("Purchase Order HTML not found", "error");
        setLoading(false);
        return;
      }

      // Create a temporary element to hold the HTML with styling
      const element = document.createElement('div');
      element.innerHTML = `
        <style>
          body { font-size: 10px; }
          table { border-collapse: collapse; width: 100%; font-size: 10px; }
          th, td { border: 1px solid #000; padding: 8px; text-align: left; font-size: 10px; }
          th { background-color: #f5f5f5; font-weight: bold; }
        </style>
        ${response.data.html}
      `;

      const opt = {
        margin: 0.5,
        filename: `PO_${monthName}_${year}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      await html2pdf().set(opt).from(element).save();
      showToast(`Purchase Order for ${monthName} ${year} downloaded successfully`, "success");
      setLoading(false);
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to download Purchase Order. Please try again.";
      showToast(errorMessage, "error");
      console.error("Error downloading PO:", err);
      setLoading(false);
    }
  };

  const handleDownloadFinalReport = async () => {
    try {
      if (!selectedMonth) {
        showToast("Please select a month first", "warning");
        return;
      }

      setLoading(true);

      const [year, monthNum] = selectedMonth.split('-');
      const monthName = moment(`${year}-${monthNum}`, 'YYYY-MM').format('MMMM');

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-purchase-order-by-month?month=${monthName}&year=${year}`,
        { withCredentials: true }
      );

      if (!response.data || !response.data.deliveredDetailsHtml) {
        showToast("Final Report not available yet", "error");
        setLoading(false);
        return;
      }

      // Create a temporary element to hold the HTML with styling
      const element = document.createElement('div');
      element.innerHTML = `
        <style>
          body { font-size: 10px; }
          table { border-collapse: collapse; width: 100%; font-size: 10px; }
          th, td { border: 1px solid #000; padding: 8px; text-align: left; font-size: 10px; }
          th { background-color: #f5f5f5; font-weight: bold; }
        </style>
        ${response.data.deliveredDetailsHtml}
      `;

      const opt = {
        margin: 0.5,
        filename: `Final_Report_${monthName}_${year}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      await html2pdf().set(opt).from(element).save();
      showToast(`Final Report for ${monthName} ${year} downloaded successfully`, "success");
      setLoading(false);
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to download Final Report. Please try again.";
      showToast(errorMessage, "error");
      console.error("Error downloading Final Report:", err);
      setLoading(false);
    }
  };



  const handleApprove = async (video) => {
    try {
      setLoading(true);
      // 👉 Get the PUBLISH phase date of the video being submitted
      const publishPhase = video.phases?.find(
        (phase) => phase.name === "PUBLISH"
      );
      if (!publishPhase?.endDate) {
        setLoading(false);
        showToast("The video you are submitting does not have a valid PUBLISH phase date.", "error");
        return;
      }
      const videoDate = moment(publishPhase.endDate);
      const videoMonth = videoDate.month() + 1; // moment: 0-based
      const videoYear = videoDate.year();

      // 👉 Calculate the previous month/year relative to the video
      const prevDate = moment(videoDate).subtract(1, "month");
      const prevMonth = prevDate.month() + 1;
      const prevYear = prevDate.year();

      const hasPoReadyVideoLastMonth = videos.some((v) => {
        const vPublishPhase = v.phases?.find(
          (phase) => phase.name === "PUBLISH"
        );
        if (!vPublishPhase?.endDate) return false;

        const vDate = moment(vPublishPhase.endDate);
        const vMonth = vDate.month() + 1;
        const vYear = vDate.year();

        return (
          vMonth === prevMonth &&
          vYear === prevYear &&
          v.status === "APPROVED_FOR_PO"
        );
      });

      if (hasPoReadyVideoLastMonth) {
        setLoading(false);
        showToast(
          `PO is still not generated for ${prevMonth}/${prevYear}. Cannot approve more videos until the PO for ${prevMonth}/${prevYear} is generated`,
          "warning"
        );
        return;
      }


      const formattedData = {
        videoId: video._id,
        month: videoMonth,
        year: videoYear,
        videos: {
          status: "APPROVED_FOR_PO",
          phases: video.phases,
          topic: video.topic
        },
      };

      await axios.put(
        `${process.env.REACT_APP_API_URL}/update-video/`,
        formattedData,
        {
          withCredentials: true,
        },
      );
      fetchVideosAndUser();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showToast("Failed to approve video. Please try again.", "error");
      console.error("Error approving video:", err);
    }
  };

  const handleReject = async (video) => {
    try {
      setLoading(true);
      const formattedData = {
        videoId: video._id,
        videos: {
          status: "NOT_APPROVED_FOR_PO",
        },
      };
      await axios.put(
        `${process.env.REACT_APP_API_URL}/update-video/`,
        formattedData,
        {
          withCredentials: true,
        },
      );
      fetchVideosAndUser();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showToast("Failed to reject video. Please try again.", "error");
      console.error("Error rejecting video:", err);
    }
  };

  const handleEdit = (video) => {
    const formattedData = {
      _id: video._id,
      videos: {
        topic: video.topic,
        phases: video.phases,
      },
      month: video.plan?.month,
      year: video.plan?.year,
      status: video?.status,
      planId: video.plan._id,
      videoStyle: video?.videoStyle,
      storyType: video?.storyType,
    };

    navigate("/update-video", {
      state: { existingData: formattedData },
    });
  };

  const handleCloseSubmission = async () => {
    try {
      const openSubmissionVideos = submissionOpenVideos;
      if (openSubmissionVideos.length > 0) {
        // Collect all publish phase endDates
        const publishDates = openSubmissionVideos
          .map(video => video?.phases?.find(phase => phase.name === "PUBLISH")?.endDate)
          .filter(Boolean)
          .map(date => new Date(date));
        if (publishDates.length > 0) {
          const earliestDate = new Date(Math.min(...publishDates));
          const latestDate = new Date(Math.max(...publishDates));
          const formattedEarliest = earliestDate.toLocaleDateString("en-IN", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
          const formattedLatest = latestDate.toLocaleDateString("en-IN", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
          const confirmClose = window.confirm(
            `You are about to close the submission window for publish period: ${formattedEarliest} to ${formattedLatest}. \n\nThe action is permanent and cannot be reversed. \n\nDo you want to proceed?`
          );
          if (!confirmClose) return;
        }

        setLoading(true);
        await axios.post(
          `${process.env.REACT_APP_API_URL}/close-submission-window`,
          {},
          { withCredentials: true }
        );
        showToast("Submission window closed successfully", "success");
        fetchVideosAndUser();
      } else {
        showToast("Nothing to close", "info");
      }
    } catch (err) {
      showToast("Failed to close submission window. Please try again.", "error");
      console.error("Error closing submission window:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSubmissionWindow = async () => {
    try {
      if (poGeneratedVideos.length > 0) {
        // Collect all publish phase endDates
        const publishDates = poGeneratedVideos
          .map(video => video?.phases?.find(phase => phase.name === "PUBLISH")?.endDate)
          .filter(Boolean)
          .map(date => new Date(date));

        if (publishDates.length > 0) {
          const earliestDate = new Date(Math.min(...publishDates));
          const latestDate = new Date(Math.max(...publishDates));
          const formattedEarliest = earliestDate.toLocaleDateString("en-IN", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
          const formattedLatest = latestDate.toLocaleDateString("en-IN", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
          const confirmClose = window.confirm(
            `You are about to open the submission window for publish period: ${formattedEarliest} to ${formattedLatest}. \n\nDo you want to proceed?`
          );
          if (!confirmClose) return;
        }

        setLoading(true);
        await axios.post(
          `${process.env.REACT_APP_API_URL}/open-submission-window`,
          {},
          { withCredentials: true }
        );
        showToast("Submission window opened successfully", "success");
        fetchVideosAndUser();
      } else {
        showToast("No videos eligible to open submission for", "warning");
      }
    } catch (err) {
      showToast("Failed to open submission window. Please try again.", "error");
      console.error("Error trying to open submission window:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePO = async () => {
    try {
      if (approvedForPoVideos.length > 0) {
        // Collect all publish phase endDates
        const publishDates = approvedForPoVideos
          .map(video => video?.phases?.find(phase => phase.name === "PUBLISH")?.endDate)
          .filter(Boolean)
          .map(date => new Date(date));

        if (publishDates.length > 0) {
          const earliestDate = new Date(Math.min(...publishDates));
          const latestDate = new Date(Math.max(...publishDates));
          const formattedEarliest = earliestDate.toLocaleDateString("en-IN", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
          const formattedLatest = latestDate.toLocaleDateString("en-IN", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
          const confirmClose = window.confirm(
            `You are about to open the submission window for publish period: ${formattedEarliest} to ${formattedLatest}. \n\nDo you want to proceed?`
          );
          if (!confirmClose) return;
        }

        setLoading(true);
        await axios.post(
          `${process.env.REACT_APP_API_URL}/generate-po`,
          {},
          { withCredentials: true }
        );
        showToast("PO approved successfully", "success");
        fetchVideosAndUser();
      } else {
        showToast("No videos eligible to generate PO for", "warning");
      }
    } catch (err) {
      showToast("Failed to generate PO. Please try again.", "error");
      console.error("Error trying to generate PO:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {

      case "APPROVED_FOR_PO":
        return "bg-blue-100 text-blue-800"; // Blue family

      case "PO_GENERATED":
        return "bg-blue-300 text-blue-900"; // Blue, slightly darker

      case "SUBMISSION_OPEN":
        return "bg-yellow-100 text-yellow-800"; // Yellow family

      case "VIDEO_SUBMITTED":
        return "bg-green-100 text-green-800"; // Green family

      case "SUBMISSION_ACCEPTED":
        return "bg-green-300 text-green-900"; // Green, deeper shade

      case "SUBMISSION_NOT_ACCEPTED":
        return "bg-red-300 text-red-900"; // Red family

      case "PAYMENT_DONE":
        return "bg-green-500 text-white"; // Green, strong emphasis

      case "NOT_APPROVED_FOR_PO":
        return "bg-orange-100 text-orange-800"; // Orange family

      case "SUBMISSION_CLOSED_WITHOUT_VIDEO":
        return "bg-red-100 text-black"; // Gray family

      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex">
        <Sidebar
          activeSidebarItem="videos"
          onSidebarItemClick={(itemId, path) => {
            if (path) navigate(path);
          }}
        />
        <div className="flex-1">
          <div className="h-screen flex items-center justify-center">
            <DashboardLoader />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar
        activeSidebarItem="videos"
        onSidebarItemClick={(itemId, path) => {
          if (path) navigate(path);
        }}
        onLogout={() => {
          document.cookie =
            "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC";
          window.location.href = "/login";
        }}
      />

      <div className="flex-1">
        <div className="max-w-7xl mx-auto">
          <div>
            {/* Show month selection screen if no month is selected */}
            {monthSelectionMode ? (
              <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl mx-auto mt-12">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 mb-3">Select a Month</h1>
                  <p className="text-lg text-gray-600">
                    Choose a month to view videos from that period
                  </p>
                </div>

                {monthLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-96 overflow-y-auto p-4">
                    {availableMonths.map((month) => (
                      <button
                        key={month.value}
                        onClick={() => handleInitialMonthSelection(month.value)}
                        className="px-6 py-4 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 group"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-semibold text-gray-900 group-hover:text-blue-700">
                              {month.label}
                            </p>
                            {month.count && (
                              <>
                                <p className="text-sm text-gray-500 mt-1">
                                  {month.count} video{month.count !== 1 ? 's' : ''}
                                </p>
                                {month.acceptancePercentage !== undefined && (
                                  <p className="text-sm text-gray-500">
                                    {month.acceptancePercentage}% accepted
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                          <ChevronDown className="h-5 w-5 text-gray-400 transform -rotate-90 group-hover:text-blue-600" />
                        </div>
                      </button>
                    ))}

                    {availableMonths.length === 0 && (
                      <div className="col-span-full text-center py-12">
                        <p className="text-gray-500">No months available</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : isPreview ? (
              <PurchaseOrderPreview formValues={invoiceData} />
            ) : (
              <div>
                {/* Month Selector Bar */}
                <div className="mb-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">Select Month</h2>
                        <p className="text-sm text-gray-500 mt-1">
                          View videos by month for faster loading
                        </p>
                      </div>
                      <MonthSelector
                        selectedMonth={selectedMonth}
                        onMonthSelect={handleMonthChange}
                        availableMonths={availableMonths}
                        loading={monthLoading}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <h1 className="text-2xl font-bold text-gray-900">Videos</h1>
                        <p className="text-sm text-gray-500 mt-1">
                          {moment(selectedMonth, 'YYYY-MM').format('MMMM YYYY')}
                        </p>
                      </div>
                      <div className="flex items-center space-x-4">
                        {monthLoading ? (
                          <span className="text-sm text-gray-500">Loading...</span>
                        ) : (
                          <span className="text-sm text-gray-500">{videos.length} videos</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Loading State for Month Change */}
                  {monthLoading && (
                    <div className="flex justify-center items-center py-12">
                      <Loader className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                  )}

                  {/* Mobile View - Cards */}
                  {!monthLoading && (
                    <div className="block lg:hidden p-4">
                    {videos && videos.map((video) => (
                      <VideoCard
                        key={video._id}
                        video={video}
                        user={user}
                        expandedVideo={expandedVideo}
                        setExpandedVideo={setExpandedVideo}
                        handleEdit={(v) => navigate(`/update-video?id=${v._id}`)}
                        handleDelete={(v) => {
                          setVideoToDelete(v);
                          setShowDeleteModal(true);
                        }}
                        handleApprove={handleApprove}
                        handleReject={handleReject}
                        handleUnapproveVideo={handleUnapproveVideo}
                        setSelectedVideo={setSelectedVideo}
                        setShowSubmitModal={setShowSubmitModal}
                        loading={loading}
                        userRole={userRole}
                      />
                    ))}
                    </div>
                  )}

                  {/* Desktop View - Table */}
                  {!monthLoading && (
                    <div className="hidden lg:block overflow-x-auto">
                    <table className="table-fixed min-w-full divide-y divide-gray-200" style={{ width: "100%" }}>

                      <thead className="bg-gray-50">
                        <tr>
                          <th className="w-[5%] px-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="w-[3%] px-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Len
                          </th>
                          <th className="w-[10%] px-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Style
                          </th>
                          <th className="w-[12%] px-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Story Type
                          </th>
                          <th className="w-[50%] px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Topic
                          </th>

                          <th className="w-[10%] px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="w-[10%] px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {videos && videos?.map((video) => (
                          <React.Fragment key={video._id}>
                            <tr className="hover:bg-gray-50">
                              <td className="w-[5%] whitespace-normal break-words text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {video?.phases?.some(phase => phase.name === "PUBLISH") ? (
                                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full ">
                                    {
                                      moment(video.phases.find(phase => phase.name === "PUBLISH")?.endDate).format("MMM D, YYYY")
                                    }
                                  </span>
                                ) : (
                                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-gray-700 bg-gray-100">
                                    No Publish Phase
                                  </span>
                                )}
                              </td>
                              <td className="w-[3%] whitespace-normal break-words text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <span
                                  className="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                                >
                                  {video?.storyType?.durationRange}
                                </span>
                              </td>
                              <td className="w-[10%] whitespace-normal break-words text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <span
                                  className="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                                >
                                  {video?.videoStyle?.name?.split('_').join(' ')}
                                </span>
                              </td>
                              <td className="w-[12%] whitespace-normal break-words text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <span
                                  className="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                                >
                                  {video?.storyType?.name
                                    ?.toLowerCase()
                                    .split('_')
                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' ')
                                  }
                                </span>
                              </td>
                              <td className="w-[50%] whitespace-normal break-words text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <button
                                  onClick={() =>
                                    setExpandedVideo(
                                      expandedVideo === video._id ? null : video._id,
                                    )
                                  }
                                  className="flex items-center text-sm font-medium text-black font-bold"
                                >
                                  {expandedVideo === video._id ? (
                                    <ChevronUp className="h-5 w-5 mr-2 text-gray-500" />
                                  ) : (
                                    <ChevronDown className="h-5 w-5 mr-2 text-gray-500" />
                                  )}
                                  {video.topic}
                                </button>
                              </td>

                              <td className="w-[10%] whitespace-normal break-words text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(video?.status)}`}
                                >
                                  {formatStatus(video?.status)}
                                </span>
                              </td>
                              <td className="w-[10%] px-2 whitespace-normal break-words text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {video.status === "PENDING_APPROVAL" &&
                                  user?.role &&
                                  (user?.role === "ADMIN" ||
                                    user?.role === "APPROVER") ? (
                                  <div className="flex items-center ">
                                    <button
                                      onClick={() => handleApprove(video)}
                                      disabled={loading}
                                      className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Approve"
                                      aria-label="Approve video"
                                    >
                                      <Check className="h-4 w-4" />
                                    </button>

                                    <button
                                      onClick={() => handleReject(video)}
                                      disabled={loading}
                                      className="px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Reject"
                                      aria-label="Reject video"
                                    >
                                      <XCircle className="h-4 w-4" />
                                    </button>
                                  </div>

                                ) : video.status === "PURCHASE_ORDER_GENERATED" ? (
                                  <div className="flex space-x-3">
                                    <button
                                      onClick={() => {
                                        setSelectedVideo(video);
                                        setShowSubmitModal(true);
                                      }}
                                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                      Submit Video
                                    </button>
                                  </div>
                                ) : video.status === "REQUEST_CHANGES" || video.status === "PENDING_APPROVAL" ? (
                                  <div className="flex space-x-3">
                                    {/* <button
                                      onClick={() => handleEdit(video)}
                                      className="text-blue-600 hover:text-blue-900"
                                    >
                                      <Pencil className="h-5 w-5" />
                                    </button> */}
                                    <button
                                      onClick={() => {
                                        setVideoToDelete(video);
                                        setShowDeleteModal(true);
                                      }}
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </button>
                                  </div>
                                ) :
                                  (
                                    <div className="flex space-x-3">
                                    </div>
                                  )}
                                {video.status === "SUBMISSION_OPEN" && (
                                  <button
                                    onClick={() => {
                                      setSelectedVideo(video);
                                      setShowSubmitModal(true);
                                    }}
                                    className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                    title="Submit URLs"
                                    aria-label="Submit video URLs"
                                  >
                                    <Send className="h-4 w-4" />
                                  </button>
                                )}

                                {video.status === "SUBMISSION_NOT_ACCEPTED" && (
                                  <button
                                    onClick={() => {
                                      setSelectedVideo(video);
                                      setShowSubmitModal(true);
                                    }}
                                    className="px-3 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                    title="Re-submit URLs"
                                    aria-label="Re-submit video URLs"
                                  >
                                    <RefreshCw className="h-4 w-4" />
                                  </button>
                                )}

                                {(video.status === "APPROVED_FOR_PO" || video.status === "NOT_APPROVED_FOR_PO") && (user?.role &&
                                  (user?.role === "ADMIN" ||
                                    user?.role === "APPROVER")) && (
                                    <button
                                      onClick={() => {
                                        handleUnapproveVideo(video);
                                      }}
                                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                      title="Unapprove"
                                      aria-label="Unapprove video"
                                    >
                                      <RefreshCw className="h-4 w-4" />
                                    </button>
                                  )}


                              </td>
                            </tr>
                            {expandedVideo === video._id && (
                              <tr>
                                <td colSpan="4" className="px-6 py-4 bg-gray-50">
                                  <div className="space-y-4">
                                    <h4 className="font-medium text-gray-900">
                                      Links:
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                      {video.videoLinks?.map((links, index) => (
                                        <div
                                          key={index}
                                          className="bg-white p-4 rounded-lg shadow-sm border border-gray-200"
                                        >
                                          <div className="font-medium text-gray-900 mb-2 w-full">
                                            <a
                                              href={links}
                                              className="text-blue-500 hover:underline block overflow-hidden text-ellipsis whitespace-nowrap"
                                              target="_blank"
                                            >
                                              {links}
                                            </a>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="space-y-4">
                                    <h4 className="font-medium text-gray-900">
                                      Phases:
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                      {video.phases?.map((phase, index) => (
                                        <div
                                          key={index}
                                          className="bg-white p-4 rounded-lg shadow-sm border border-gray-200"
                                        >
                                          <div className="font-medium text-gray-900 mb-2">
                                            {phase?.name.replace(/_/g, " ")}
                                          </div>
                                          <div className="text-sm text-gray-500">
                                            <div>
                                              Start: {formatDate(phase.startDate)}
                                            </div>
                                            <div>
                                              End: {formatDate(phase.endDate)}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )}

                  {videos?.length === 0 && !loading && !monthLoading && (
                    <div className="text-center py-12">
                      <div className="text-gray-500">
                        <p className="text-lg font-medium">No videos found</p>
                        <p className="text-sm mt-2">
                          No videos available for {moment(selectedMonth, 'YYYY-MM').format('MMMM YYYY')}
                        </p>
                        <p className="text-sm mt-1">
                          Try selecting a different month
                        </p>
                      </div>
                    </div>
                  )}

                  {userRole === "ADMIN" && (
                  <table>
                    <tr>
                      <td className="pr-6">
                        <div>
                          <button onClick={handleGeneratePO} className="px-4 py-2 mt-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm transition-colors">
                            Generate Purchase Order
                          </button>
                        </div>
                      </td>
                      <td className="pr-6">
                        <div>
                          <button onClick={handleVideoReview} className="px-4 py-2 mt-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm transition-colors">
                            Run Video Review
                          </button>
                        </div>
                      </td>

                      <td className="pr-6">
                        <div>
                          <button onClick={handleOpenSubmissionWindow} className="px-4 py-2 mt-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm transition-colors">
                            Open Submission Window
                          </button>
                        </div>
                      </td>

                      <td className="pr-6">
                        <div>
                          <button onClick={handleCloseSubmission} className="px-4 py-2 mt-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm transition-colors">
                            Close Submission Window
                          </button>
                        </div>
                      </td>

                      <td className="pr-6">
                        <div>
                          <button onClick={handleDownloadPurchaseOrder} className="px-4 py-2 mt-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 shadow-sm transition-colors">
                            Download Purchase Order
                          </button>
                        </div>
                      </td>

                      <td className="pr-6">
                        <div>
                          <button onClick={handleDownloadFinalReport} className="px-4 py-2 mt-2 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 shadow-sm transition-colors">
                            Download Final Report
                          </button>
                        </div>
                      </td>
                    </tr>
                  </table>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0  overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">


            <div className="inline-block z-20 align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Video
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete the video "
                        {videoToDelete?.topic}"? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => handleDelete(videoToDelete)}
                  disabled={deleteLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >

                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Submit Video Modal */}
      {showSubmitModal && (
        <VideoSubmitModal
          setShowSubmitModal={setShowSubmitModal}
          selectedVideo={selectedVideo}
          fetchVideos={fetchVideos}
          setVideos={setVideos}
        />
      )}

    </div>
  );
};

export default ViewVideos;

